import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TenantDetailModal = ({ tenant, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !tenant) return null;

  const usageData = [
    { month: 'Jul', users: 32, storage: 45, apiCalls: 12500 },
    { month: 'Aug', users: 38, storage: 52, apiCalls: 15200 },
    { month: 'Sep', users: 41, storage: 48, apiCalls: 18900 },
    { month: 'Oct', users: 45, storage: 55, apiCalls: 22100 },
    { month: 'Nov', users: 43, storage: 58, apiCalls: 19800 },
    { month: 'Dec', users: 47, storage: 62, apiCalls: 24500 },
    { month: 'Jan', users: 45, storage: 59, apiCalls: 23200 }
  ];

  const billingHistory = [
    {
      id: 'inv-001',
      date: '2025-01-01',
      amount: 2500,
      status: 'paid',
      description: 'Enterprise Plan - January 2025'
    },
    {
      id: 'inv-002',
      date: '2024-12-01',
      amount: 2500,
      status: 'paid',
      description: 'Enterprise Plan - December 2024'
    },
    {
      id: 'inv-003',
      date: '2024-11-01',
      amount: 2500,
      status: 'paid',
      description: 'Enterprise Plan - November 2024'
    },
    {
      id: 'inv-004',
      date: '2024-10-01',
      amount: 2200,
      status: 'paid',
      description: 'Professional Plan - October 2024'
    }
  ];

  const supportTickets = [
    {
      id: 'ticket-001',
      title: 'API Integration Issues',
      status: 'open',
      priority: 'high',
      created: '2025-01-24T10:30:00Z',
      assignee: 'Sarah Johnson'
    },
    {
      id: 'ticket-002',
      title: 'User Permission Settings',
      status: 'resolved',
      priority: 'medium',
      created: '2025-01-20T14:15:00Z',
      assignee: 'Mike Chen'
    },
    {
      id: 'ticket-003',
      title: 'Dashboard Performance',
      status: 'in-progress',
      priority: 'low',
      created: '2025-01-18T09:45:00Z',
      assignee: 'Alex Rodriguez'
    }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-success/10 text-success';
      case 'pending': return 'bg-warning/10 text-warning';
      case 'overdue': return 'bg-error/10 text-error';
      case 'open': return 'bg-error/10 text-error';
      case 'in-progress': return 'bg-warning/10 text-warning';
      case 'resolved': return 'bg-success/10 text-success';
      default: return 'bg-muted text-text-secondary';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-text-secondary';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'usage', label: 'Usage Analytics', icon: 'Activity' },
    { id: 'billing', label: 'Billing History', icon: 'CreditCard' },
    { id: 'support', label: 'Support Tickets', icon: 'HeadphonesIcon' }
  ];

  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Users" size={20} className="text-primary" />
            <span className="text-xs text-success">+12%</span>
          </div>
          <div className="text-2xl font-bold text-text-primary">{tenant.userCount}</div>
          <div className="text-xs text-text-secondary">Active Users</div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="DollarSign" size={20} className="text-success" />
            <span className="text-xs text-success">+8%</span>
          </div>
          <div className="text-2xl font-bold text-text-primary">{formatCurrency(tenant.monthlyRevenue)}</div>
          <div className="text-xs text-text-secondary">Monthly Revenue</div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Heart" size={20} className="text-error" />
            <span className="text-xs text-success">+5%</span>
          </div>
          <div className="text-2xl font-bold text-text-primary">{tenant.healthScore}</div>
          <div className="text-xs text-text-secondary">Health Score</div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Calendar" size={20} className="text-warning" />
            <span className="text-xs text-text-secondary">48 days</span>
          </div>
          <div className="text-sm font-medium text-text-primary">{formatDate(tenant.renewalDate)}</div>
          <div className="text-xs text-text-secondary">Renewal Date</div>
        </div>
      </div>

      {/* Company Information */}
      <div className="bg-muted/30 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-text-primary mb-4">Company Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-text-secondary">Company Name</label>
              <p className="text-text-primary">{tenant.companyName}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary">Subscription Tier</label>
              <p className="text-text-primary">{tenant.subscriptionTier}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary">Account Status</label>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(tenant.status)}`}>
                {tenant.status}
              </span>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-text-secondary">Risk Level</label>
              <p className={`font-medium capitalize ${getPriorityColor(tenant.riskLevel)}`}>
                {tenant.riskLevel} Risk
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary">Last Activity</label>
              <p className="text-text-primary">{formatDate(tenant.lastActivity)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary">Tenant ID</label>
              <p className="text-text-primary font-mono text-sm">{tenant.id}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const UsageTab = () => (
    <div className="space-y-6">
      <div className="bg-muted/30 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-text-primary mb-4">Usage Trends</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={usageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-text-secondary)" />
              <YAxis stroke="var(--color-text-secondary)" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-popover)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
              />
              <Line type="monotone" dataKey="users" stroke="var(--color-primary)" strokeWidth={2} />
              <Line type="monotone" dataKey="storage" stroke="var(--color-secondary)" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-muted/30 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-text-primary mb-4">API Usage</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={usageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-text-secondary)" />
              <YAxis stroke="var(--color-text-secondary)" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-popover)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="apiCalls" fill="var(--color-accent)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  const BillingTab = () => (
    <div className="space-y-6">
      <div className="bg-muted/30 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-text-primary mb-4">Billing History</h4>
        <div className="space-y-3">
          {billingHistory.map((invoice) => (
            <div key={invoice.id} className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={20} className="text-primary" />
                </div>
                <div>
                  <h5 className="font-medium text-text-primary">{invoice.description}</h5>
                  <p className="text-sm text-text-secondary">{formatDate(invoice.date)} • {invoice.id}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="font-semibold text-text-primary">{formatCurrency(invoice.amount)}</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(invoice.status)}`}>
                    {invoice.status}
                  </span>
                </div>
                <Button variant="ghost" size="sm">
                  <Icon name="Download" size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const SupportTab = () => (
    <div className="space-y-6">
      <div className="bg-muted/30 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-text-primary mb-4">Support Tickets</h4>
        <div className="space-y-3">
          {supportTickets.map((ticket) => (
            <div key={ticket.id} className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="HeadphonesIcon" size={20} className="text-warning" />
                </div>
                <div>
                  <h5 className="font-medium text-text-primary">{ticket.title}</h5>
                  <p className="text-sm text-text-secondary">
                    {formatDate(ticket.created)} • Assigned to {ticket.assignee}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(ticket.status)}`}>
                    {ticket.status}
                  </span>
                  <p className={`text-sm font-medium ${getPriorityColor(ticket.priority)}`}>
                    {ticket.priority} priority
                  </p>
                </div>
                <Button variant="ghost" size="sm">
                  <Icon name="ExternalLink" size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
        
        <div className="relative bg-card border border-border rounded-lg shadow-elevation-4 w-full max-w-6xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-muted">
                <img
                  src={tenant.logo}
                  alt={tenant.companyName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-text-primary">{tenant.companyName}</h2>
                <p className="text-sm text-text-secondary">{tenant.subscriptionTier} Plan • {tenant.id}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm">
                <Icon name="Mail" size={16} />
                Contact
              </Button>
              <Button variant="outline" size="sm">
                <Icon name="Settings" size={16} />
                Manage
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-border">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors
                    ${activeTab === tab.id
                      ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary'
                    }
                  `}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {activeTab === 'overview' && <OverviewTab />}
            {activeTab === 'usage' && <UsageTab />}
            {activeTab === 'billing' && <BillingTab />}
            {activeTab === 'support' && <SupportTab />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantDetailModal;